<script setup lang="ts">
import { computed, defineComponent, h, reactive, ref, onBeforeMount } from 'vue'
import { colors, PopupBasicHeader } from 'wepin-vue-component'
import { PopupList } from '@/components'
import type {
  TypePopupItem,
  TypePopupListItem,
} from '@/components/popup/list/PopupList.vue'
import { useI18nTypes, useBreakPointsClass } from '@/composables'
import { defaultBreakPoints } from '@/const'
import {
  WalletStateExported as _WalletStateExported,
  WalletStateVerified as _WalletStateVerified,
} from '@/const/backendDbMeaning'
import { useLoadingsStore, useWalletAPIStore, useWalletStore } from '@/stores'
const props = defineProps<{
  walletId: string
}>()

const emits = defineEmits<{
  (event: 'select-route', name: string, walletId: string): void
  (event: 'close'): void
}>()
const walletAPIStore = useWalletAPIStore()
const walletStore = useWalletStore()
const { t } = useI18nTypes()
const { breakPointsClass } = useBreakPointsClass(defaultBreakPoints)
const loadingsStore = useLoadingsStore()
const isDefaultWallet = computed(() => {
  return walletStore.getWallet(props.walletId)?.defaultWallet
})
onBeforeMount(() => {
  const selectedWallet = walletStore.getWallet(props.walletId)
  if (selectedWallet) {
    haveAccount.value = selectedWallet.accounts.length > 0
    //
    if (
      selectedWallet.state === 'EXPORTED' ||
      selectedWallet.state === 'VERIFIED'
    ) {
      backupListMenu.desc = t('wallet_manage.is_backuped')
    }
  }

  if (!isDefaultWallet.value && !haveAccount.value) {
    listItems.value.push({
      type: 'item',
      name: t('wallet_manage.delete'),
      button: {
        type: 'element',
        name: 'Right',
        size: 'medium',
        color: colors.currentThemeRef.value.grey_111,
      },
      onClick() {
        const walletState = walletStore.walletList.find(
          (wallet) => wallet.id === props.walletId,
        )?.state
        // 백업이 완료된 지갑(VERIFIED 또는 EXPORTED)은 바로 삭제 확인으로 이동
        if (walletState === 'VERIFIED' || walletState === 'EXPORTED') {
          emits('select-route', 'delete_wallet_Exec', props.walletId)
        } else {
          // 백업되지 않은 지갑은 백업 안내 화면으로 이동
          emits('select-route', 'delete_wallet_start', props.walletId)
        }
      },
    })
  }
})
const haveAccount = ref(false)
const toggleListItem: TypePopupListItem = reactive({
  type: 'item',
  name: t('wallet_manage.default_wallet'),
  button: {
    type: 'toggle',
    checked: isDefaultWallet.value as boolean,
    color: colors.currentThemeRef.value.main,
    disabled: false,
  },
  async onCheck(event: boolean) {
    if (isDefaultWallet.value) return
    if (toggleListItem.button?.type === 'toggle') {
      loadingsStore.openSpinner()
      try {
        const result = await walletAPIStore.updateWallet(props.walletId, {
          defaultWallet: true,
        })
        if (!result) return
        toggleListItem.button.checked = event
        // toggleListItem.button.disabled = result.wallet.defaultWallet
        //
        if (result.wallet.defaultWallet && listItems.value.length === 5) {
          listItems.value.pop()
        }
      } finally {
        loadingsStore.closeSpinner()
      }
    }
  },
})

const backupListMenu: TypePopupListItem = reactive({
  type: 'item',
  name: t('wallet_manage.backup'),
  button: {
    type: 'element',
    name: 'Right',
    size: 'medium',
    color: colors.currentThemeRef.value.grey_111,
  },
  onClick() {
    emits('select-route', 'backup_confirm_caution', props.walletId)
  },
})

const listItems = ref<TypePopupItem[]>([
  /* [기본 지갑 변경] 임시 주석처리
  toggleListItem,
  {
    type: 'line',
  },
  */
  {
    type: 'item',
    name: t('wallet_manage.label_modify'),
    button: {
      type: 'element',
      name: 'Right',
      size: 'medium',
      color: colors.currentThemeRef.value.grey_111,
    },
    onClick() {
      emits('select-route', 'change_wallet_label', props.walletId)
    },
  },
  {
    type: 'item',
    name: t('wallet_manage.change_pin.title'),
    button: {
      type: 'element',
      name: 'Right',
      size: 'medium',
      color: colors.currentThemeRef.value.grey_111,
    },
    onClick() {
      emits('select-route', 'change_pin', props.walletId)
    },
  },
  backupListMenu,
])

const onClose = () => {
  emits('close')
}
</script>
<script lang="ts">
// eslint-disable-next-line import/order
import WalletManage from './WalletManage.vue'
export const createWalletManageRoute = (
  onSelectRoute: (name: string, walletId: string) => void,
) => {
  return defineComponent<typeof WalletManage>((props) => {
    return () =>
      h(WalletManage, {
        'onSelect-route': (name: string, walletId: string) => {
          onSelectRoute(name, walletId)
        },
        walletId: props.walletId,
      })
  })
}
</script>

<template>
  <div class="wallet-manage" :class="[breakPointsClass]">
    <PopupBasicHeader :title="t('wallet_manage.title')" :exit="onClose" />
    <div class="popup-body">
      <PopupList :items="listItems" />
      <div v-if="isDefaultWallet" class="text-item">
        <span>{{ t('wallet_manage.delete') }}</span>
        <span>{{ t('wallet_manage.delete_disabled') }}</span>
      </div>
      <div v-else-if="haveAccount" class="text-item">
        <span>{{ t('wallet_manage.delete') }}</span>
        <span>{{ t('wallet_manage.delete_disabled_account') }}</span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.wallet-manage {
  width: 360px;
  background-color: var(--grey_fff);
  border-radius: 8px;
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
  .popup-body {
    padding: 0 30px 10px;
    .text-item {
      // margin-bottom: 20px;
      color: var(--grey_999);
      display: grid;
      grid-template-columns: 1fr auto;
      grid-template-areas: 'name desc';
      cursor: pointer;
      align-items: center;
      margin-bottom: 20px;
      padding: {
        top: 7px;
        bottom: 17px;
      }
      & > span:first-of-type {
        grid-area: name;
        @include typeface('Paragraph_16_sb');
      }
      & > span:last-of-type {
        grid-area: desc;
        @include typeface('Paragraph_14_r');
      }
    }
  }
}

.wallet-manage.mobile {
  width: 100%;
  .popup-body {
    padding: 0 20px;
  }
}
</style>
